import * as Sentry from '@sentry/browser';
import ChoicesSelect, { Choice } from 'choices.js';
import { createCustomSelect } from '@components';

// Definice rozhraní pro položky modelu pro response data
interface ModelItem {
  name: string;
  alias: string;
}

interface DataTrackClick {
  event: string;
  clickTo: string;
  link: {
    type: string;
    text: string;
    url: string;
    location: string;
  };
}

// Funkce pro načítání dat z API
const fetchData = async (url: string): Promise<ModelItem[]> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Error fetching data, Status: ${response.status}`);
    }
    return (await response.json()) as ModelItem[];
  } catch (error) {
    Sentry.captureException(`Jakebox: Error fetching data: ${(error as Error).message}`);
    throw new Error('Error fetching data');
  }
};

// Nastavení výchozí volby v selectu
const defaultOption: Choice = {
  value: '',
  label: 'Zvolte model auta',
  disabled: true,
  selected: true,
  placeholder: true,
};

// Funkce pro aktualizaci výběru modelu
const updateModelSelect = async (
  modelSelectInstance: ChoicesSelect,
  items: ModelItem[],
  maker: string
) => {
  let choices: Choice[] = [defaultOption];

  modelSelectInstance.clearChoices();

  if (items.length === 0) {
    // Pokud nejsou žádné položky, nastavíme pouze výchozí volbu
    await modelSelectInstance.setChoices(choices, 'value', 'label', false);

    // Opět odstraníme options aby se zobrazily potřebné hlášky
    modelSelectInstance.clearChoices();
  } else {
    // Jinak přidáme položky získané z API
    choices = choices.concat(
      items.map((item: ModelItem) => ({ value: item.alias, label: item.name }))
    );
    await modelSelectInstance.setChoices(choices, 'value', 'label', false);

    // Pokud jsou k dispozici data ve window.jakeData, vybereme odpovídající model
    if (window.jakeData && window.jakeData.maker === maker && window.jakeData.model) {
      modelSelectInstance.setChoiceByValue(window.jakeData.model);
    }
  }
};

// Funkce pro zpracování změny modelu
const handleModelChange = async (maker: string, modelSelectInstance: ChoicesSelect) => {
  const url = `${document.location.origin}/export/jakeAuto/${maker}`;
  try {
    const items = await fetchData(url);
    await updateModelSelect(modelSelectInstance, items, maker);
  } catch (error) {
    Sentry.captureException(`Jakebox: Error handling model change: ${(error as Error).message}`);
  }
};

// Funkce pro inicializaci komponenty JakeBox
export const JakeBox = (jakeBoxElement: HTMLElement) => {
  // defaultní selectboxy pro výběr výrobce a modelu
  const makerSelectElement: HTMLSelectElement | null =
    jakeBoxElement.querySelector('[data-jakebox-maker]');
  const modelSelectElement: HTMLSelectElement | null =
    jakeBoxElement.querySelector('[data-jakebox-models]');
  const submitButton = document.querySelector('[data-jakebox-submit]');

  // Pokud nejsou nalezeny potřebné elementy, ukončíme funkci
  if (!makerSelectElement || !modelSelectElement) return;

  // Nastavení možností pro výběr výrobce a modelu
  const makerSelectOptions = {
    searchEnabled: true,
  };

  const modelSelectOptions = {
    noChoicesText: 'U dané značky nejsou k dispozici žádné modely.',
  };

  // Vytvoření instancí pro výběr výrobce a modelu
  const makerSelectInstance = createCustomSelect(makerSelectElement, makerSelectOptions);
  const modelSelectInstance = createCustomSelect(modelSelectElement, modelSelectOptions);

  // Pokud nebyly vytvořeny instance, ukončíme funkci
  if (!makerSelectInstance || !modelSelectInstance) return;

  // Funkce pro přednastavení výrobce
  const preselectMaker = () => {
    if (window.jakeData && window.jakeData.maker) {
      const { maker } = window.jakeData;

      makerSelectInstance.setChoiceByValue(maker);

      // Zavolání funkce pro zpracování změny modelu s přednastaveným výrobcem
      handleModelChange(maker, modelSelectInstance).catch((err) => {
        Sentry.captureException(`Jakebox: Error preselecting maker: ${(err as Error).message}`);
      });
    }
  };

  const preventAutofill = () => {
    const inputs = jakeBoxElement.querySelectorAll('select, input');
    inputs.forEach((input) => {
      if (input instanceof HTMLSelectElement && !input.value) {
        input.setAttribute('autocomplete', 'off');
        input.setAttribute('data-lpignore', 'true');
      }
    });
  };

  // Inicializace komponenty
  const init = () => {
    preselectMaker();

    preventAutofill();
  };

  // Nastavení posluchače události změny výrobce
  makerSelectElement.addEventListener('change', () => {
    handleModelChange(makerSelectElement.value, modelSelectInstance).catch((err) => {
      Sentry.captureException(`Jakebox: Error handling model change: ${(err as Error).message}`);
    });
  });

  const updateGATracking = (url: string) => {
    if (!submitButton) return;
    const dataTrackClickAttribute = submitButton.getAttribute('data-track-click');
    if (!dataTrackClickAttribute) return;

    const dataTrackClick = JSON.parse(dataTrackClickAttribute) as DataTrackClick;
    dataTrackClick.link.url = url;
    submitButton.setAttribute('data-track-click', JSON.stringify(dataTrackClick));
  };

  // Nastavení posluchače události odeslání formuláře
  const handleSubmit = (event: Event) => {
    event.preventDefault();

    if (makerSelectElement?.value) {
      const maker = makerSelectElement.value || '';
      const model = modelSelectElement.value || '';

      if (!maker) return;

      const url = `${document.location.origin}/${maker}/${model}`;

      updateGATracking(url);

      window.location.href = url;
    }
  };

  // Přidání posluchače události pro tlačítko odeslání formuláře
  if (submitButton instanceof HTMLButtonElement) {
    submitButton.addEventListener('click', handleSubmit);
  }

  // Spuštění inicializace komponenty
  init();

  // Návrat objektu s instancemi pro výběr výrobce a modelu pro externi manipulaci
  return {
    makerSelect: makerSelectInstance,
    modelSelect: modelSelectInstance,
  };
};

export default JakeBox;
